import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import MerakiSDK from "@urbanpiper-engineering/meraki-sdk";

import ItemDetailCard from "@urbanpiper-engineering/meraki-components/dist/Molecules/ItemDetailCard";
import RecommendationCarousel from "@urbanpiper-engineering/meraki-components/dist/Cells/RecommendationCarousel";
import ItemCard from "@urbanpiper-engineering/meraki-components/dist/Molecules/ItemCard";
import FloatingStack from "@urbanpiper-engineering/meraki-components/dist/Atoms/FloatingStack";
import Button from "@urbanpiper-engineering/meraki-components/dist/Atoms/Button";
import AddToCart from "@urbanpiper-engineering/meraki-components/dist/Molecules/AddToCart";

import type { CartState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/cart/states/cart.state";

import { getStaticProps as getInitialProps } from "../../../hooks/server.props";
import { onItemUpdate } from "../../../hooks/cart.util";
import useIframe from "../../../hooks/iframe.hook";
import ITEM_DATA from "../../../editor-static-data/item-details.json";
import SeoData from "../../../components/seo_data";
import { useIsMobile } from "../../../hooks/is_mobile.hook";

import styles from "./item.module.scss";
import pageSelector from "../../../external/pageSelector";
import configSelector from "../../../external/configSelector";
import { LazyImage } from "../../../components/lazy-image";

export const getStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});

export const getStaticProps = async (context: any) => {
  const init = await getInitialProps(context);

  const query = context.params;
  const id = Number(query?.id);
  const likeButton =
    pageSelector.getMenuPage()?.customizations?.likeButton ?? null;

  const showRecommendedItems =
    pageSelector.getItemDetailPage()?.customizations?.recommendedItems;
  let itemInfo: any = null;
  let seoData: any = null;
  let openGraphData: any = null;

  try {
    if (id > 0) {
      itemInfo = await MerakiSDK.getItemById({
        item_id: id,
      });

      if (itemInfo?.id) {
        itemInfo.isAlcoholic = MerakiSDK.isItemAlcoholic(itemInfo);
        seoData = {
          title: itemInfo?.item_title || null,
          description: itemInfo?.item_desc || null,
          keywords: itemInfo?.item_title || null,
        };

        const primaryDomain = configSelector.getPrimaryDomain();

        const slug = itemInfo?.slug ? itemInfo.slug : query?.slug;
        openGraphData = {
          title: itemInfo?.item_title || null,
          description: itemInfo?.item_desc || null,
          url:
            primaryDomain?.url?.length > 0
              ? `https://${primaryDomain?.url}/item/${itemInfo?.id}/${slug}`
              : null,
          image: itemInfo?.image_url || null,
        };
      }
    }
  } catch (error) {
    console.log(error, "itemInfoError");
  }

  return {
    props: {
      ...init.props,
      id,
      showRecommendedItems,
      _itemInfo: itemInfo,
      seoData,
      openGraphData,
      likeButton,
    },
    revalidate: 300,
  };
};

function ItemDetail({
  _itemInfo = null,
  showRecommendedItems = false,
  seoData,
  openGraphData,
  likeButton,
}: {
  readonly _itemInfo: any;
  readonly showRecommendedItems: boolean;
  readonly seoData: any;
  readonly openGraphData: any;
  likeButton: boolean;
}) {
  const isMobile = useIsMobile();
  const [itemInfo, setItemInfo] = useState<any>(_itemInfo);
  const router = useRouter();
  const { t } = useTranslation();
  const useCartState = MerakiSDK.getCartState().hook();
  const [favouritesList, setFavouritesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cartItems, cartItemQty] = useCartState((state: typeof CartState) => [
    state.cart?.items || [],
    state.cart?.item_count,
  ]);
  const { loadedInIframe } = useIframe();

  useEffect(() => {
    (async () => {
      try {
        let itemId = Number(_itemInfo?.id || router?.query?.id);
        if (!itemId) {
          const fallbackQuery = {
            id: window.location.pathname.split("/")[2],
            slug: window.location.pathname.split("/")[3],
          };

          itemId = Number(fallbackQuery?.id);
        }

        let newItemInfo = await MerakiSDK.getItemById({
          item_id: itemId,
        });
        newItemInfo.isAlcoholic = MerakiSDK.isItemAlcoholic(newItemInfo);
        if (newItemInfo?.id) {
          setItemInfo(newItemInfo);
        }
      } catch (error) {
        console.log(error, "itemInfoError");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const cartItemQuantityMap = useMemo(
    () =>
      cartItems.reduce((acc: any, item: any) => {
        if (!acc[item.id]) {
          acc[item.id] = 0;
        }
        acc[item.id] += item.quantity;
        return acc;
      }, {}),
    [cartItems, cartItemQty],
  );

  const [recommendedItems, setRecommendedItems] = React.useState([]);

  useEffect(() => {
    if (!itemInfo?.id) {
      if (loadedInIframe) {
        setItemInfo(ITEM_DATA.itemInfo);

        if (showRecommendedItems) {
          setRecommendedItems(ITEM_DATA.recommendedItems);
        } else {
          setRecommendedItems([]);
        }
      }
    } else {
      if (showRecommendedItems) {
        MerakiSDK.getRecommendedItems(itemInfo?.id).then((res: any) => {
          if (res?.length > 0) {
            res.forEach((item: any) => {
              item.isAlcoholic = MerakiSDK.isItemAlcoholic(item);
            });
            setRecommendedItems(res);
          }
        });
      }
    }

    fetchFavourites();
    return () => {
      if (showRecommendedItems) {
        setRecommendedItems([]);
      }
    };
  }, [itemInfo, showRecommendedItems, loadedInIframe]);

  const fetchFavourites = () => {
    return MerakiSDK.getFavorites()
      .then((res: any) => {
        if (res?.status !== "error") {
          setFavouritesList(res?.map((item: any) => item.item.id));
        }
      })
      .catch((err: any) => {
        console.log(err, "err");
      });
  };

  const toggleFavourite = async (item: any, status: boolean) => {
    if (status) {
      await MerakiSDK.addItemAsFavourite(item.id);
    } else {
      await MerakiSDK.removeItemAsFavourite(item.id);
    }
  };

  if (!_itemInfo) {
    return null;
  }

  return (
    <div className={styles.item}>
      <h1 className="visually-hidden">{seoData?.title || "Product Details"}</h1>
      <SeoData seoData={seoData} openGraphData={openGraphData} />
      <div className={`container`}>
        <ItemDetailCard
          item={itemInfo}
          count={cartItemQuantityMap[itemInfo?.id] || 0}
          onClickShare={() => {}}
          onItemUpdate={onItemUpdate}
          isLoading={isLoading}
          isFavourite={favouritesList?.includes(itemInfo?.id)}
          isFavouriteEnabled={likeButton}
          toggleFavourite={(status: boolean) => {
            toggleFavourite(itemInfo, status);
          }}
          // imageComponent={LazyImage}
        />
        {recommendedItems?.length > 0 && (
          <RecommendationCarousel itemlistLength={recommendedItems?.length}>
            {recommendedItems.map((item: any) => {
              return (
                <ItemCard
                  {...item}
                  count={cartItemQuantityMap[item.id] || 0}
                  key={item.id}
                  onItemUpdate={onItemUpdate}
                  onClickCallback={() => {
                    router.push(`/item/${item.id}/${item.slug}`);
                  }}
                  isFavourite={favouritesList?.includes(item?.id)}
                  isFavouriteEnabled={likeButton}
                  toggleFavourite={(status: boolean) => {
                    toggleFavourite(item, status);
                  }}
                />
              );
            })}
          </RecommendationCarousel>
        )}
        {isMobile && (
          <FloatingStack>
            <div className={styles.item_detail__customize_button}>
              {itemInfo?.has_options && !cartItemQuantityMap[itemInfo?.id] ? (
                <Button
                  size="lg"
                  variant="solid"
                  onClick={async () =>
                    await onItemUpdate({ ...itemInfo, quantity: 0 }, 1)
                  }
                >
                  {t("itemCard.customize")}
                </Button>
              ) : (
                <AddToCart
                  classes="item-detail-card__add-to-cart"
                  quantity={cartItemQuantityMap[itemInfo?.id] || 0}
                  loading={false}
                  stock={itemInfo?.current_stock}
                  variant="solid"
                  onItemUpdate={async (
                    id: number,
                    quantity: number,
                    dir: number,
                  ) => {
                    onItemUpdate &&
                      (await onItemUpdate(
                        {
                          quantity,
                          id,
                          ...itemInfo,
                        },
                        dir,
                      ));
                  }}
                  itemId={itemInfo?.id}
                />
              )}
            </div>
          </FloatingStack>
        )}
      </div>
    </div>
  );
}

export default ItemDetail;
